import * as React from 'react'
import { Puzzle, Swords } from 'lucide-react'
import { ActionButton } from '../../library/ActionButton/ActionButton'
import { FocusNavigationDialog } from '../../library/FocusNavigationDialog/FocusNavigationDialog'
import { GameControlButton } from '../GameControls/GameControlButton'
import { useTurnIndicatorModel } from '../../mateList/list/TurnIndicator/useTurnIndicatorModel'
import { useTheme } from '../../../contexts/ThemeContext'
import type { ChallengeConfiguration } from '@merels/room'

export interface ChallengeConfigurationControlProps {
  activeChallengeConfiguration?: ChallengeConfiguration
  availableChallengeConfigurations: ChallengeConfiguration[]
  onSaveAndRestart(challengeConfiguration: ChallengeConfiguration): Promise<void>
  accentColor?: string
  accentInkColor?: string
  backgroundColor?: string
}

function configurationId(configuration: ChallengeConfiguration): string {
  return configuration.challengeSource === 'opponent'
    ? 'opponent'
    : `game-${configuration.gameChallengeArrangement}`
}

function configurationLabel(configuration: ChallengeConfiguration): string {
  if (configuration.challengeSource === 'opponent') return 'Play against each other'
  if (configuration.gameChallengeArrangement === 'cooperative') return 'Solve a challenge together'
  if (configuration.gameChallengeArrangement === 'sameChallengeRace') return 'Solve the same challenge competitively'
  return 'Solve different challenges competitively'
}

/** Opens the challenge-source picker while keeping the header compact. */
export function ChallengeConfigurationControl({
  activeChallengeConfiguration = { challengeSource: 'opponent' },
  availableChallengeConfigurations,
  onSaveAndRestart,
  accentColor,
  accentInkColor,
  backgroundColor,
}: ChallengeConfigurationControlProps) {
  const { getAppColor } = useTheme()
  const turnIndicator = useTurnIndicatorModel()
  const [open, setOpen] = React.useState(false)
  const [draft, setDraft] = React.useState<ChallengeConfiguration>(activeChallengeConfiguration)
  const [isSaving, setIsSaving] = React.useState(false)
  const isGameChallenge = activeChallengeConfiguration.challengeSource === 'game'
  const appColor = getAppColor()
  const controlAccentColor = accentColor ?? turnIndicator?.accentColor ?? appColor.main
  const controlAccentInkColor = accentInkColor ?? turnIndicator?.accentInkColor ?? '#ffffff'
  const controlBackgroundColor = backgroundColor ?? turnIndicator?.backdropColor ?? appColor.background

  const openDialog = () => {
    setDraft(activeChallengeConfiguration)
    setOpen(true)
  }

  const saveAndRestart = async () => {
    setIsSaving(true)
    try {
      await onSaveAndRestart(draft)
      setOpen(false)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <FocusNavigationDialog open={open} onOpenChange={setOpen} title="Challenge source" navigationItems={availableChallengeConfigurations.map((configuration) => ({ id: configurationId(configuration) }))}
      trigger={<GameControlButton title="Change challenge source" ariaLabel="Change challenge source" onClick={openDialog} icon={isGameChallenge ? <Puzzle size="var(--menu-icon-size)" /> : <Swords size="var(--menu-icon-size)" />} accentColor={controlAccentColor} accentInkColor={controlAccentInkColor} backgroundColor={controlBackgroundColor} />}
    >
      <p className="mb-4 text-sm text-muted">Choose who establishes the challenge. Saving restarts this game for everyone.</p>
      <div className="space-y-2">
        {availableChallengeConfigurations.map((configuration) => (
          <label key={configurationId(configuration)} className="flex cursor-pointer items-center gap-2 rounded border border-basic bg-surface p-3 text-sm !text-primary">
            <input type="radio" name="challenge-configuration" checked={configurationId(draft) === configurationId(configuration)} onChange={() => setDraft(configuration)} />
            {configuration.challengeSource === 'opponent' ? <Swords size={18} /> : <Puzzle size={18} />}
            {configurationLabel(configuration)}
          </label>
        ))}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <ActionButton variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>Cancel</ActionButton>
        <ActionButton variant="default" accentBackground onClick={() => void saveAndRestart()} disabled={isSaving}>Save and Restart</ActionButton>
      </div>
    </FocusNavigationDialog>
  )
}
